import { ClockIcon, MinusIcon, PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { InputType } from 'config'
import { customOptionLabels, editableOptionFields } from 'config/loan.constants'
import { getLoanInputFieldOptions, getLoanInputFields, InputTypeOfLoans } from 'config/loan.input.fields.constants'
import {
  CHANNEL_VISIBLE_LOGIC_FIELD_KEY,
  FieldOrigin,
  fieldOriginForVisibility,
  fieldOriginOptions,
  isCustomOrigin,
  IVisibleLogic,
  LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN,
  LoanFieldProps,
  Operator,
} from 'config/loan.input.visibility.type'
import { extendedFieldOriginOptions } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/inputs'
import { useContext, useMemo, useRef, useState } from 'react'
import { ReactReduxContext, useSelector } from 'react-redux'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { capitalizeFirstLetter } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { Tooltip } from '../Tooltip/Tooltip'

const isBlurOnChangeInput = (inputType?: string) => {
  const type = inputType?.toLowerCase()
  return type === 'text' || type === 'textarea' || type === 'multiselect' || type === 'googlemap'
}

type FieldTitleWithOptions = { title: string; fields: Record<string, string> }

type RenamableOptionInput = Extract<
  InputType,
  {
    inputType:
      | 'select'
      | 'Select'
      | 'OptionSelect'
      | 'multiselect'
      | 'MultiSelect'
      | 'radio'
      | 'Radio'
      | 'searchableselect'
      | 'SearchableSelect'
      | 'buttongroup'
      | 'ButtonGroup'
      | 'ButtonGroupSelector'
      | 'buttongroupselector'
  }
>

const isRenamableOptionInput = (input: InputType): input is RenamableOptionInput => {
  const type = input.inputType?.toLowerCase()
  return (
    type === 'select' ||
    type === 'optionselect' ||
    type === 'multiselect' ||
    type === 'radio' ||
    type === 'searchableselect' ||
    type === 'buttongroup' ||
    type === 'buttongroupselector'
  )
}

interface InputVisibleLogicProps {
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Id of Input
   */
  id: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Error
   */
  error?: string

  tooltip?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: IVisibleLogic[][]

  fieldTitles?: Record<string, FieldTitleWithOptions> | null
  fieldInputs?: InputTypeOfLoans | null
  loanFieldProperties?: LoanFieldProps | null

  /**
   * Custom class name
   */
  className?: string
  /**
   * Optional history handler
   */
  history?: boolean
  additionalElements?: JSX.Element | Function | null
  options?: {
    hasCustomOrigin?: boolean
    hasOtherOrigin?: boolean
    useMultiSelectForSelectInput?: boolean
    additionalFieldOrigins?: Record<
      string,
      {
        title: string
        inputs: Record<string, InputType>
      }
    >
    hasNotVisible?: boolean
  }
  zIndex?: number
  onChange?: (checked: any) => void
  showHistory?: () => void
}

const selectLoanFieldProperties = (state: any) =>
  state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps

/**
 * Primary UI component for user interaction.
 * The confirm dialog mounts this outside the app Provider, so the store is read only when that context exists.
 */
export const InputVisibleLogic = (props: InputVisibleLogicProps) => {
  const reduxContext = useContext(ReactReduxContext)
  if (!reduxContext) return <InputVisibleLogicView {...props} />
  return <InputVisibleLogicFromStore {...props} />
}

const InputVisibleLogicFromStore = (props: InputVisibleLogicProps) => {
  const reduxLoanFieldProperties = useSelector(selectLoanFieldProperties)
  return <InputVisibleLogicView {...props} reduxLoanFieldProperties={reduxLoanFieldProperties} />
}

const InputVisibleLogicView = ({
  disabled = false,
  id = '',
  title = '',
  error = '',
  tooltip = '',
  value = [],
  history = false,
  className = '',
  additionalElements = null,
  fieldTitles = null,
  fieldInputs = null,
  loanFieldProperties: _loanFieldProperties = null,
  options = {},
  zIndex = 10,
  onChange = () => {},
  showHistory = () => {},
  reduxLoanFieldProperties,
}: InputVisibleLogicProps & { reduxLoanFieldProperties?: LoanFieldProps }) => {
  const _now = Date.now()
  const draftValuesRef = useRef<Record<string, any>>({})
  const [, setDraftRevision] = useState(0)

  const {
    hasCustomOrigin = false,
    hasOtherOrigin = false,
    useMultiSelectForSelectInput = true,
    additionalFieldOrigins,
    hasNotVisible = false,
  } = options
  const loanFieldProperties = _loanFieldProperties || reduxLoanFieldProperties

  const loanInputFields = fieldInputs || getLoanInputFields({ custom: hasCustomOrigin, other: hasOtherOrigin })

  const getCustomTitle = (fieldOrigin: FieldOrigin) => {
    let title = loanFieldProperties?.customTitle?.[fieldOrigin] || ''

    if (!title && isCustomOrigin(fieldOrigin)) title = extendedFieldOriginOptions[FieldOrigin.CustomInformation]

    return title || extendedFieldOriginOptions[fieldOrigin]
  }

  const newFieldTitles = useMemo(() => {
    if (fieldTitles) return fieldTitles

    const result: Record<string, FieldTitleWithOptions> = {}
    const fieldOrigins = Object.keys(loanFieldProperties ?? {})
    if (hasCustomOrigin) fieldOrigins.push(FieldOrigin.Custom)

    fieldOrigins.forEach((_fieldOrigin) => {
      const fieldOrigin = _fieldOrigin as FieldOrigin
      const fields = loanInputFields?.[fieldOrigin]
      if (!fields) return

      const titles: Record<string, string> = {}
      const title = getCustomTitle(fieldOrigin)
      if (!title) return

      Object.keys(fields).forEach((key) => {
        const title = loanFieldProperties?.[fieldOrigin]?.[key]?.title || fields[key].title
        if (!title) return
        titles[key] = title
      })

      if (!Object.keys(titles).length) return
      result[fieldOrigin] = {
        title,
        fields: titles,
      }
    })

    return result
  }, [fieldTitles])

  const loanInputFieldOptions = getLoanInputFieldOptions('', {}, fieldInputs, newFieldTitles)

  const isBorrowerSection = (fieldOrigin: string) => {
    const sectionType = loanFieldProperties?.properties?.[fieldOrigin as FieldOrigin]?.sectionType
    return sectionType === undefined || sectionType === 'Borrower'
  }

  const fieldOriginOptionsWithCustom = useMemo(() => {
    let options: Record<string, string> = {}
    Object.keys(newFieldTitles)
      .filter(isBorrowerSection)
      .forEach((fieldOrigin) => {
        options[fieldOrigin] = newFieldTitles[fieldOrigin].title
      })

    fieldOriginForVisibility
      .filter(isBorrowerSection)
      .forEach((fieldOrigin) => (options[fieldOrigin] = fieldOriginOptions[fieldOrigin]))

    if (!hasOtherOrigin) delete (options as any)[FieldOrigin.Other]
    if (!hasCustomOrigin) delete (options as any)[FieldOrigin.Custom]
    // delete (options as any)[FieldOrigin.CustomInformation]

    delete (options as any).sign

    if (additionalFieldOrigins) {
      Object.keys(additionalFieldOrigins).forEach((fieldOrigin) => {
        options[fieldOrigin] = additionalFieldOrigins[fieldOrigin].title
      })
    }

    return options
  }, [newFieldTitles, additionalFieldOrigins, hasOtherOrigin, hasCustomOrigin])

  const onAddVisibleLogic = (orIndex: number) => {
    const newValue = cloneDeep(value)

    if (orIndex == -1) newValue.push([])
    else
      newValue[orIndex].push({
        fieldOrigin: null,
        fieldKey: '',
        value: '',
      } as any)
    onChange(newValue)
  }

  const onRemoveVisibleLogic = (orIndex: number, andIndex: number = -1) => {
    const newValue = cloneDeep(value)

    if (andIndex == -1) newValue.splice(orIndex, 1)
    else newValue[orIndex].splice(andIndex, 1)

    onChange(newValue)
  }

  const onUpdateVisibleLogic = (orIndex: number, andIndex: number, key: keyof IVisibleLogic, _value: any) => {
    const newValue = cloneDeep(value)

    let newValueItem = newValue![orIndex][andIndex]
    if (key == 'fieldOrigin') {
      newValueItem.fieldKey = ''
      ;(newValueItem as any).value = null
    }
    let nextValue = _value
    if (
      key === 'value' &&
      (newValueItem.fieldOrigin === LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN ||
        newValueItem.fieldOrigin === 'Channel') &&
      newValueItem.fieldKey === CHANNEL_VISIBLE_LOGIC_FIELD_KEY &&
      Array.isArray(_value)
    ) {
      const allKeys = Object.keys(getLoanTypeMapForSelect())
      if (!_value.length || (allKeys.length > 0 && allKeys.every((k) => _value.includes(k)))) nextValue = []
    }
    ;(newValueItem as any)[key] = nextValue

    newValue![orIndex][andIndex] = newValueItem
    onChange(newValue)
  }

  const getFieldOptions = (fieldOrigin: FieldOrigin) => {
    let options = loanInputFieldOptions[fieldOrigin]
    if (options) return options

    options = {}
    const additionalFieldOrigin = additionalFieldOrigins?.[fieldOrigin]
    if (additionalFieldOrigin) {
      Object.keys(additionalFieldOrigin.inputs).forEach((key) => {
        const input = additionalFieldOrigin.inputs[key]
        options[key] = `${input.title} [${capitalizeFirstLetter(input.inputType)}]`
      })
    }
    return options
  }

  const getFieldInputs = (fieldOrigin: FieldOrigin, fieldKey: string): InputType => {
    const inputs = loanInputFields?.[fieldOrigin]?.[fieldKey]
    const withRenamedOptions = (input: InputType): InputType => {
      if (!editableOptionFields.includes(fieldKey) || !isRenamableOptionInput(input)) return input
      const labels = customOptionLabels(loanFieldProperties?.[fieldOrigin]?.[fieldKey]?.options as any)
      if (!labels) return input
      return { ...input, options: labels }
    }
    if (inputs) return withRenamedOptions(inputs)
    const additionalFieldOrigin = additionalFieldOrigins?.[fieldOrigin]
    if (additionalFieldOrigin?.inputs?.[fieldKey]) return withRenamedOptions(additionalFieldOrigin.inputs[fieldKey])
    return {
      inputType: 'text',
      title: '',
    }
  }

  const renderVisibleLogic = (option: IVisibleLogic, orIndex: number, andIndex: number) => {
    const { fieldOrigin, fieldKey, value } = option
    const inputs: Record<string, InputType> = {
      fieldOrigin: {
        inputType: 'select',
        title: 'Page Origin',
        options: fieldOriginOptionsWithCustom,
        hasDefaultOption: true,
        value: fieldOrigin,
        required: true,
        sort: false,
      },
      fieldKey: {
        inputType: 'select',
        title: 'Select Field',
        options: getFieldOptions(fieldOrigin),
        hasDefaultOption: true,
        value: fieldKey,
        required: true,
        sort: true,
      },
    }
    if (fieldOrigin && fieldKey) {
      inputs.value = {
        ...getFieldInputs(fieldOrigin, fieldKey),
        value: value as any,
      }
      if (useMultiSelectForSelectInput && inputs.value?.inputType?.toLowerCase() == 'select') {
        const input = inputs.value
        input.inputType = 'multiselect'
        let displayValue: any = !value ? [] : typeof value === 'string' ? [value] : value
        if (
          (fieldOrigin === LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN || fieldOrigin === 'Channel') &&
          fieldKey === CHANNEL_VISIBLE_LOGIC_FIELD_KEY &&
          (!displayValue || !displayValue.length)
        ) {
          displayValue = Object.keys(getLoanTypeMapForSelect())
        }
        input.value = displayValue
        input.defaultSelected = false
        inputs.value = input as any
      }
    }
    return (
      <div className="pl-4">
        <div className="flex justify-between">
          <p className="text-sm">
            Condition {orIndex + 1} - {andIndex + 1} (AND)
          </p>
          <div className="flex gap-2">
            {hasNotVisible &&
              (!option.operator ? (
                <Tooltip message="Visible when Equal">
                  <div
                    className="btn-icon text-lg"
                    onClick={() => onUpdateVisibleLogic(orIndex, andIndex, 'operator', Operator.NotEqual)}
                  >
                    <p className="w-4 h-4 leading-4 text-center">=</p>
                  </div>
                </Tooltip>
              ) : (
                <Tooltip message="Visible when Not Equal">
                  <div
                    className="btn-icon text-lg"
                    onClick={() => onUpdateVisibleLogic(orIndex, andIndex, 'operator', Operator.Equal)}
                  >
                    <p className="w-4 h-4 leading-4 text-center">≠</p>
                  </div>
                </Tooltip>
              ))}
            <span className="btn-icon" onClick={() => onRemoveVisibleLogic(orIndex, andIndex)}>
              <MinusIcon className="w-4 h-4" />
            </span>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-x-2">
          {Object.keys(inputs).map((key) => {
            const elementKey = `${key}-${fieldOrigin}-${fieldKey}-${key}`
            const inputDef = inputs[key]
            const draftKey = `${orIndex}-${andIndex}-${key}`
            const blurOnChange = isBlurOnChangeInput(inputDef.inputType)
            const draftValue = draftValuesRef.current[draftKey]
            const resolvedInput =
              blurOnChange && draftValue !== undefined ? { ...inputDef, value: draftValue } : inputDef

            return (
              <div
                className={`mb-4 ${!['fieldOrigin', 'fieldKey'].includes(key) ? 'col-span-full' : ''}`}
                key={elementKey}
              >
                <RenderInput
                  input={resolvedInput}
                  Key={elementKey}
                  onChange={(_key: string, newValue: any) => {
                    if (blurOnChange) {
                      draftValuesRef.current[draftKey] = newValue
                      setDraftRevision((n) => n + 1)
                      return
                    }
                    onUpdateVisibleLogic(orIndex, andIndex, key as any, newValue)
                  }}
                  onBlur={
                    blurOnChange
                      ? () => {
                          const pendingValue = draftValuesRef.current[draftKey]
                          if (pendingValue !== undefined && pendingValue !== value) {
                            onUpdateVisibleLogic(orIndex, andIndex, key as any, pendingValue)
                          }
                          delete draftValuesRef.current[draftKey]
                          setDraftRevision((n) => n + 1)
                        }
                      : undefined
                  }
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderVisibleLogicAND = (logics: IVisibleLogic[], orIndex: number) => {
    return (
      <div className={`${orIndex % 2 == 0 ? `bg-slate-100` : ''} p-2`}>
        <div className="flex justify-between items-center">
          <p className="font-bold text-sm">{orIndex + 1}. Visible Conditions (AND)</p>
          <div className="flex">
            <span className="btn-icon" onClick={() => onRemoveVisibleLogic(orIndex)}>
              <MinusIcon className="w-4 h-4" />
            </span>
            <span className="btn-icon" onClick={() => onAddVisibleLogic(orIndex)}>
              <PlusIcon className="w-4 h-4" />
            </span>
          </div>
        </div>

        {logics.map((v, andIndex) => renderVisibleLogic(v, orIndex, andIndex))}
      </div>
    )
  }

  const renderLogics = () => {
    return (
      <div>
        <div className="flex justify-between items-center">
          <p className="font-bold text-sm">Visible Conditions (OR)</p>
          <span className="btn-icon" onClick={() => onAddVisibleLogic(-1)}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>

        {value.map((v, logicIdx) => renderVisibleLogicAND(v, logicIdx))}
      </div>
    )
  }

  return (
    <div>
      <div className={`group relative z-[${zIndex}] w-full group ${className}`}>
        <div className="flex flex-1">
          {!!title && (
            <label
              htmlFor={`${id}-${_now}`}
              className={`inline-flex relative items-center cursor-pointer h-5 ${disabled && 'cursor-not-allowed'}`}
            >
              <span className="text-[14px] font-medium text-gray-900 dark:text-gray-300">{title}</span>
            </label>
          )}

          <div className="flex-1" />
          {tooltip ? (
            <Tooltip message={tooltip}>
              <QuestionMarkCircleIcon className="w-4 h-4" />
            </Tooltip>
          ) : null}
          {history && (
            <span className="ml-3 hidden group-hover:inline" onClick={() => showHistory()}>
              <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
            </span>
          )}
          {additionalElements !== null &&
            (typeof additionalElements == 'function' ? additionalElements(onChange) : additionalElements)}
        </div>
        {renderLogics()}
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1">{error}</p>}
    </div>
  )
}
